import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Template, TemplateSection } from '../../_types';

// Section metadata for smart collapse state migration
// Use compound key (name + displayOrder) to handle duplicate names
interface SectionMetadata {
  id: number; // Changed to number to match TemplateSection.id
  name: string | null; // Updated to handle nullable names from inheritance
  displayOrder: number; // Stable identifier that persists after reorder + save
}

interface StoredCollapseData {
  sections: SectionMetadata[];
  collapsed: Record<string, boolean>;
}

export const useCollapseState = (template: Template, templateId: number | null, isReorderMode: boolean) => {
  // ===== Session storage-based collapse state =====
  // Store section metadata to enable smart migration when backend assigns new IDs
  const storageKey = `phx-template-builder-collapsed-${templateId || 'new'}`;

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) return {};

      const data: StoredCollapseData = JSON.parse(stored);
      const currentSections = template.templateSections;

      // Check if stored section IDs match current ones
      const storedIds = data.sections?.map((s) => s.id) || [];
      const currentIds = currentSections.map((s) => s.id);

      // Compare numeric IDs directly
      if (storedIds.length === currentIds.length && storedIds.every((id, i) => id === currentIds[i])) {
        return data.collapsed || {};
      }

      if (data.sections && data.sections.length === currentSections.length) {
        const migratedState: Record<string, boolean> = {};
        currentSections.forEach((currentSection, index) => {
          const currentOrder = currentSection.rank ?? currentSection.displayOrder ?? index;

          const currentKey = `${currentSection.name}:${currentOrder}`;

          const matchingStoredSection = data.sections.find((s) => `${s.name}:${s.displayOrder}` === currentKey);

          if (matchingStoredSection && data.collapsed[String(matchingStoredSection.id)]) {
            migratedState[String(currentSection.id)] = true;
          }
        });

        return migratedState;
      }
      return {};
    } catch (err) {
      console.error('Failed to load collapse state:', err);
      return {};
    }
  });

  const [forceAllCollapsed, setForceAllCollapsed] = useState(false);
  const collapseSnapshotRef = useRef<Record<string, boolean>>({});
  const prevSectionIdsRef = useRef<string>('');

  useEffect(() => {
    const currentSectionIds = template.templateSections.map((s) => s.id).join(',');
    const previousSectionIds = prevSectionIdsRef.current;

    if (previousSectionIds === currentSectionIds || !currentSectionIds) {
      return;
    }

    prevSectionIdsRef.current = currentSectionIds;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) {
        return;
      }

      const data: StoredCollapseData = JSON.parse(stored);
      const storedIds = data.sections?.map((s) => s.id).join(',') || '';

      // Only migrate if stored IDs are different AND we have the same number of sections
      if (
        storedIds &&
        storedIds !== currentSectionIds &&
        data.sections &&
        data.sections.length === template.templateSections.length
      ) {
        const migratedState: Record<string, boolean> = {};

        template.templateSections.forEach((currentSection, index) => {
          const currentOrder = currentSection.rank ?? currentSection.displayOrder ?? index;
          const currentKey = `${currentSection.name}:${currentOrder}`;

          const matchingStoredSection = data.sections.find((s) => `${s.name}:${s.displayOrder}` === currentKey);

          if (matchingStoredSection && data.collapsed[String(matchingStoredSection.id)]) {
            migratedState[String(currentSection.id)] = true;
          }
        });

        if (Object.keys(migratedState).length > 0) {
          setCollapsedSections(migratedState);
        }
      }
    } catch (err) {
      console.error('Failed to migrate collapse state:', err);
    }
  }, [template.templateSections, storageKey]);

  useEffect(() => {
    try {
      const dataToStore: StoredCollapseData = {
        sections: template.templateSections.map((s, index) => ({
          id: s.id,
          name: s.name,
          // Use rank if available (from backend), otherwise use displayOrder or index
          displayOrder: s.rank ?? s.displayOrder ?? index,
        })),
        collapsed: collapsedSections,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(dataToStore));
    } catch (err) {
      console.error('Failed to save collapse state to sessionStorage', err);
    }
  }, [storageKey, collapsedSections, template.templateSections]);

  // Policy TOC-style helper functions
  const expandSections = useCallback((sectionIds: number[]) => {
    setCollapsedSections((prev) => {
      const next = { ...prev };
      let hasChanges = false;
      sectionIds.forEach((id) => {
        const key = String(id);
        if (next[key] === true) {
          delete next[key]; // Remove from collapsed list = expanded
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, []);

  const collapseSections = useCallback((sectionIds: number[]) => {
    setCollapsedSections((prev) => {
      const next = { ...prev };
      let hasChanges = false;
      sectionIds.forEach((id) => {
        const key = String(id);
        if (!next[key]) {
          next[key] = true; // Add to collapsed list
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, []);

  // Helper to apply UI collapse map (and "force all") onto any Template object
  const applyCollapseMap = useCallback(
    (tmpl: Template): Template => ({
      ...tmpl,
      templateSections: tmpl.templateSections.map((s) => ({
        ...s,
        collapsed: forceAllCollapsed ? true : collapsedSections[String(s.id)] || false,
      })),
    }),
    [collapsedSections, forceAllCollapsed]
  );

  // Reorder mode: snapshot collapse map and visually collapse all; restore after save
  const prevIsReorderModeRef = useRef(isReorderMode);
  useEffect(() => {
    if (isReorderMode && !prevIsReorderModeRef.current) {
      // Entering reorder mode: snapshot current states and collapse all
      collapseSnapshotRef.current = { ...collapsedSections };
      const allSectionIds = template.templateSections.map((s) => s.id);
      collapseSections(allSectionIds);
    } else if (!isReorderMode && prevIsReorderModeRef.current) {
      // Exiting reorder mode: restore previous states
      setCollapsedSections(collapseSnapshotRef.current);
      collapseSnapshotRef.current = {};
      setForceAllCollapsed(false);
    }

    prevIsReorderModeRef.current = isReorderMode;
  }, [isReorderMode, template.templateSections, collapsedSections, collapseSections]);

  return {
    collapsedSections,
    expandSections,
    collapseSections,
    applyCollapseMap,
  };
};
